import React, { Component } from "react";
import mapboxgl from "mapbox-gl";
import ReactGA from "react-ga";
import { ErrorBoundary } from "react-error-boundary";
import { toLngLat } from "../../utils/coordinates";

// mapboxgl.accessToken = 'pk.eyJ1IjoiaW50dWdpbmV0ZWNobm9sb2dpZXMiLCJhIjoiY2s1NTk1N2tmMDdzMTNsbzNqMXN1MHdwaiJ9.v_0VaJjBB4Z_deeQ_WulHw';
// mapboxgl.accessToken = `pk.eyJ1IjoibG92ZXByZWV0c2luZ2giLCJhIjoiY2xqZm53aDhkMDJ0NTNsdXBmbGZwd3NjNiJ9.tyfH6qc7p4v-UFzNc8Rkww`;
mapboxgl.accessToken = `pk.eyJ1IjoiYXl1c2hpbnR1Z2luZSIsImEiOiJjbHMxaWdramYwZzV3Mm1tazU4aTA5MzlkIn0.2EpIwfNReCEYLuSmPRT3Ag`;

const buildHeatmapCollections = (data = {}) => {
  const heatmapData1 = {};

  Object.keys(data).forEach((slice) => {
    const points = data[slice];
    if (!Array.isArray(points)) return;

    heatmapData1[slice] = {
      type: "FeatureCollection",
      features: [],
    };

    points.forEach((element, index) => {
      const coordinates = toLngLat(element);
      if (!coordinates) return;

      heatmapData1[slice].features.push({
        type: "Feature",
        properties: {
          id: `${slice}-heatmap-${index}`,
        },
        geometry: {
          type: "Point",
          coordinates,
        },
      });
    });
  });

  return heatmapData1;
};

export class Map extends Component {
  constructor(props) {
    super(props);

    this.state = {
      lng: 78.9629,
      lat: 20.5937,
      zoom: 4,
      legend: [],
      isLoaded: false,
    };

    this.legend = [];
  }

  componentDidMount() {
    this.map = new mapboxgl.Map({
      container: this.mapContainer,
      style: "mapbox://styles/mapbox/dark-v10",
      center: [78.9629, 20.5937],
      zoom: 4,
    });

    this.map.addControl(new mapboxgl.FullscreenControl());
    this.map.addControl(new mapboxgl.NavigationControl());
    this.map.on("load", () => {
      this.setState((prevState) => ({ ...prevState, isLoaded: true }));
      this.updateHeatmapLayers(this.props.data);
      this.map.resize();
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const dataChanged = prevProps.data !== this.props.data;
    const mapReady = this.state.isLoaded && !prevState.isLoaded;

    if ((dataChanged || mapReady) && this.map && this.state.isLoaded) {
      this.updateHeatmapLayers(this.props.data);
    }
  }

  updateHeatmapLayers(data) {
    const heatmapData1 = buildHeatmapCollections(data);
    const legendStore = [];

    Object.keys(heatmapData1).forEach((key) => {
      let circleColour = [];
      let color = "#11b4da";
      if (this.props.covidUser) {
        circleColour = [
          "step",
          ["get", "point_count"],
          "#51bbd6",
          100,
          "#f1f075",
          201,
          "#f28cb1",
        ];
      } else {
        if (key === "source") {
          color = "#f1f075";
          circleColour = [
            "step",
            ["get", "point_count"],
            "#f1f075",
            100,
            "#f1f075",
            750,
            "#f1f075",
          ];
          legendStore.push({ name: "Source", color: "#f1f075" });
        } else {
          color = "#28AE14";
          circleColour = [
            "step",
            ["get", "point_count"],
            "#28AE14",
            100,
            "#28AE14",
            750,
            "#28AE14",
          ];
          legendStore.push({ name: "Destination", color: "#28AE14" });
        }
      }

      if (this.map) {
        const layerPrefix = `cutomMarker-${key}`;

        if (this.map.getLayer(`${layerPrefix}-clusters`))
          this.map.removeLayer(`${layerPrefix}-clusters`);

        if (this.map.getLayer(`${layerPrefix}-cluster-count`))
          this.map.removeLayer(`${layerPrefix}-cluster-count`);

        if (this.map.getLayer(`${layerPrefix}-unclustered`))
          this.map.removeLayer(`${layerPrefix}-unclustered`);

        if (this.map.getSource(layerPrefix))
          this.map.removeSource(layerPrefix);

        if (!heatmapData1[key].features.length) return;

        this.map.addSource(layerPrefix, {
          type: "geojson",
          data: heatmapData1[key],
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
        });

        this.map.addLayer({
          id: `${layerPrefix}-clusters`,
          type: "circle",
          source: layerPrefix,
          filter: ["has", "point_count"],
          paint: {
            "circle-color": circleColour,
            "circle-radius": [
              "step",
              ["get", "point_count"],
              20,
              100,
              30,
              750,
              40,
            ],
          },
        });

        this.map.addLayer({
          id: `${layerPrefix}-cluster-count`,
          type: "symbol",
          source: layerPrefix,
          filter: ["has", "point_count"],
          layout: {
            "text-field": "{point_count_abbreviated}",
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
          },
        });

        this.map.addLayer({
          id: `${layerPrefix}-unclustered`,
          type: "circle",
          source: layerPrefix,
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-color": color,
            "circle-radius": 7,
            "circle-stroke-width": 1,
            "circle-stroke-color": "#fff",
          },
        });
      }
    });

    this.legend = legendStore;
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  logEvent(action) {
    ReactGA.event({
      category: this.props.user,
      action,
    });
  }

  render() {
    return (
      <ErrorBoundary fallback={<div>Something went wrong</div>}>
        <>
          <div
            id="map"
            ref={(el) => (this.mapContainer = el)}
            onClick={() => this.logEvent("Clicked on Map to view trip heatmap")}
          ></div>
          {!this.props.covidUser ? (
            <div id="state-legend" class="legend">
              <h4>Legend</h4>
              <div key="source">
                <span style={{ backgroundColor: "#f1f075" }}></span>Source
              </div>
              <div key="distination">
                <span style={{ backgroundColor: "#28AE14" }}></span>Destination
              </div>
            </div>
          ) : (
            <div id="state-legend" class="legend">
              <h4>Legend</h4>
              <div key="99">
                <span style={{ backgroundColor: "#51bbd6" }}></span> 0 - 99
              </div>
              <div key="100">
                <span style={{ backgroundColor: "#f1f075" }}></span>100 - 200
              </div>
              <div key="200">
                <span style={{ backgroundColor: "#f28cb1" }}></span> 200
              </div>
            </div>
          )}
        </>
      </ErrorBoundary>
    );
  }
}

export default Map;
